import React, { useMemo, useState, useEffect } from "react";
import styled from "@emotion/styled";
import { MOBILE_SCREEN } from "../../components/theme";
import { QuestionWithRelations } from "../../../../api/src/types";
import VideoContainer from "../../components/VideoContainer";
import ExampleScenario from "./ExampleScenario";
import Protip from "./Protip";
import QuestionRenderer from "./QuestionRenderer";
import { StyledDescription } from "./Questionnaire";
import { Card } from "../../components/layout/Card";
import RequiredAsterisk from "../../components/RequiredAsterisk";
import { SelectedOptionsContext } from "./utils/SelectedOptionsContext";

export const CardContainer = styled.div`
  margin: 24px 0;
  ${MOBILE_SCREEN} {
    margin: 16px 0;
  }
`;

const ReadOnlyCardContainer = styled.div`
  margin-bottom: 16px;
  ${MOBILE_SCREEN} {
    margin-bottom: 8px;
  }
`;

const ReadOnlyQuestionTitle = styled.span`
  display: flex;
  text-align: left;
  align-items: center;
  gap: 8px;
`;

interface QuestionProps {
  question: QuestionWithRelations;
  shouldValidateQuestionnaireSection?: boolean;
  isReadOnly?: boolean;
}

const Question: React.FC<QuestionProps> = ({
  question,
  shouldValidateQuestionnaireSection,
  isReadOnly = false,
}) => {
  const hasNoAnswers = question.answers.length === 0 && question.isRequired;
  const validationError =
    hasNoAnswers && Boolean(shouldValidateQuestionnaireSection);

  // Track currently selected options for instant protip display (optimistic UI)
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(
    question.answers[0]?.answerQuestionOptions?.map(
      (opt) => opt.questionOptionId,
    ) ?? [],
  );

  // Sync with server state when answers change (e.g., on initial load or after save)
  useEffect(() => {
    const serverSelectedIds =
      question.answers[0]?.answerQuestionOptions?.map(
        (opt) => opt.questionOptionId,
      ) ?? [];
    setSelectedOptionIds(serverSelectedIds);
  }, [question.answers]);

  // Memoize selected options with protips based on optimistic selection state
  const selectedOptionsWithProtips = useMemo(() => {
    const selectedIds = new Set(selectedOptionIds);
    return question.questionOptions.filter(
      (opt) => selectedIds.has(opt.id) && opt.proTip,
    );
  }, [selectedOptionIds, question.questionOptions]);

  if (isReadOnly) {
    return (
      <ReadOnlyCardContainer>
        <Card
          header={{
            title: (
              <ReadOnlyQuestionTitle>
                {question.title}
                {question.isRequired && <RequiredAsterisk />}
              </ReadOnlyQuestionTitle>
            ),
          }}
        >
          <QuestionRenderer question={question} isReadOnly={isReadOnly} />
        </Card>
      </ReadOnlyCardContainer>
    );
  }

  return (
    <CardContainer>
      <Card
        header={{
          title: (
            <>
              {question.title}
              {question.isRequired && <RequiredAsterisk />}
            </>
          ),
        }}
        isError={validationError}
      >
        {question.description && (
          <StyledDescription
            dangerouslySetInnerHTML={{
              __html: question.description,
            }}
          />
        )}
        {Array.isArray(question.videos) && question.videos.length > 0 && (
          <VideoContainer
            videoDetails={{
              youtubeLink: question.videos[0]?.youtubeLink ?? "",
              title: question.videos[0]?.title ?? "",
              author: question.videos[0]?.author ?? "",
            }}
          />
        )}
        {question.exampleScenario && (
          <ExampleScenario text={question.exampleScenario} />
        )}
        {question.proTip && <Protip text={question.proTip} />}

        {/*Render the question based on the question type*/}
        <SelectedOptionsContext.Provider
          value={{ selectedOptionIds, setSelectedOptionIds }}
        >
          <QuestionRenderer question={question} />
        </SelectedOptionsContext.Provider>

        {/* Display protips only for selected question options */}
        {selectedOptionsWithProtips.map(
          (option) =>
            option.proTip && <Protip key={option.id} text={option.proTip} />,
        )}
      </Card>
    </CardContainer>
  );
};

export default Question;
